import React from 'react';
import { useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';
import Switch from 'react-switch';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Dropdown } from 'src/components';
import { DotsThreeVertical, TrashSimple } from 'src/common/icons';

import messages from '../messages';
import styles from './SequenceBuilder.module.scss';

// A single client-side step. `id` is only used as the dnd/React key and is
// stripped before the steps are sent to the backend `sequence` op. `params`
// is intentionally minimal (only upscale `scale`); every other step runs
// with the Fal app's defaults.
export type SequenceStepUI = {
  id: string;
  type: string;
  enabled: boolean;
  params?: { scale?: number };
};

// Catalog of step types the user can compose. Order here is only the order
// shown in the "add step" menu — the pipeline order is whatever the user
// arranges. Must mirror the backend step catalog in
// `filespin/lib/imaging/steps.py` (KNOWN_STEP_TYPES).
const STEP_CATALOG: Array<{ type: string; labelId: keyof typeof messages; descId: keyof typeof messages }> = [
  { type: 'white_balance', labelId: 'aiOpWhiteBalance', descId: 'aiOpWhiteBalanceDesc' },
  { type: 'exposure', labelId: 'aiOpExposure', descId: 'aiOpExposureDesc' },
  { type: 'color', labelId: 'aiOpColor', descId: 'aiOpColorDesc' },
  { type: 'deblur', labelId: 'aiOpDeblur', descId: 'aiOpDeblurDesc' },
  { type: 'upscale', labelId: 'aiOpUpscale', descId: 'aiOpUpscaleDesc' },
  { type: 'face_restore', labelId: 'aiOpFaceRestore', descId: 'aiOpFaceRestoreDesc' },
];

// Default seed for a fresh customizable sequence. Mirrors the deterministic
// AUTO_ENHANCE_PRESET in `filespin/lib/imaging/steps.py` (a mild colour
// correction + smart sharpen); the user can then add/remove/reorder steps.
const AUTO_ENHANCE_STEP_TYPES = ['exposure', 'color', 'deblur'];

const labelIdForType = (type: string): keyof typeof messages => {
  const entry = STEP_CATALOG.find((s) => s.type === type);
  return entry ? entry.labelId : 'aiEditSequenceTitle';
};

const descIdForType = (type: string): keyof typeof messages | undefined => {
  const entry = STEP_CATALOG.find((s) => s.type === type);
  return entry ? entry.descId : undefined;
};

// Build the default seed sequence (the auto_enhance preset, all enabled).
export const makePresetSteps = (): SequenceStepUI[] =>
  AUTO_ENHANCE_STEP_TYPES.map((type) => ({ id: uuidv4(), type, enabled: true }));

// Strip client-only fields and drop empty params so the payload matches the
// backend `sequence` step shape: { type, enabled, params? }.
export const serializeSteps = (steps: SequenceStepUI[]) =>
  steps.map((s) => {
    const out: { type: string; enabled: boolean; params?: { [key: string]: any } } = {
      type: s.type,
      enabled: s.enabled,
    };
    if (s.params && Object.keys(s.params).length > 0) {
      out.params = s.params;
    }
    return out;
  });

type RowProps = {
  step: SequenceStepUI;
  disabled?: boolean;
  onToggle: (id: string) => void;
  onRemove: (id: string) => void;
  onScaleChange: (id: string, scale: number) => void;
};

const SortableRow: React.FC<RowProps> = ({ step, disabled, onToggle, onRemove, onScaleChange }) => {
  const { formatMessage } = useIntl();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: step.id,
    disabled,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 1 : undefined,
  };
  const label = formatMessage(messages[labelIdForType(step.type)]);
  const descId = descIdForType(step.type);

  return (
    <div ref={setNodeRef} style={style} className={step.enabled ? styles.row : `${styles.row} ${styles.rowOff}`}>
      <button
        type="button"
        className={styles.dragHandle}
        aria-label={formatMessage(messages.aiEditDragHint)}
        disabled={disabled}
        {...attributes}
        {...listeners}
      >
        <DotsThreeVertical />
      </button>

      <Switch
        checked={step.enabled}
        onChange={() => onToggle(step.id)}
        disabled={disabled}
        onColor="#0633de"
        height={18}
        width={36}
        handleDiameter={14}
        uncheckedIcon={false}
        checkedIcon={false}
        aria-label={formatMessage(messages.aiEditStepEnabled)}
      />

      <span className={styles.rowText}>
        <span className={step.enabled ? styles.rowLabel : `${styles.rowLabel} ${styles.rowLabelOff}`}>{label}</span>
        {descId && <span className={styles.rowDesc}>{formatMessage(messages[descId])}</span>}
      </span>

      {step.type === 'upscale' && (
        <div className={styles.scalePicker}>
          {[2, 4].map((s) => (
            <button
              key={s}
              type="button"
              disabled={disabled}
              onClick={() => onScaleChange(step.id, s)}
              className={(step.params?.scale ?? 2) === s ? `${styles.scaleChip} ${styles.scaleChipActive}` : styles.scaleChip}
            >
              {s}x
            </button>
          ))}
        </div>
      )}

      <button
        type="button"
        className={styles.removeBtn}
        onClick={() => onRemove(step.id)}
        disabled={disabled}
        aria-label={formatMessage(messages.aiEditRemoveStep)}
      >
        <TrashSimple />
      </button>
    </div>
  );
};

type SequenceBuilderProps = {
  steps: SequenceStepUI[];
  onChange: (steps: SequenceStepUI[]) => void;
  disabled?: boolean;
};

// Ordered, selectively-enabled pipeline editor. Reorder by dragging the
// handle; toggle a step on/off without removing it; upscale exposes a 2x/4x
// scale. The whole list runs as a single backend `sequence` op (one charge).
export const SequenceBuilder: React.FC<SequenceBuilderProps> = ({ steps, onChange, disabled }) => {
  const { formatMessage } = useIntl();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = steps.findIndex((s) => s.id === active.id);
    const newIndex = steps.findIndex((s) => s.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onChange(arrayMove(steps, oldIndex, newIndex));
  };

  const toggleStep = (id: string) =>
    onChange(steps.map((s) => (s.id === id ? { ...s, enabled: !s.enabled } : s)));
  const removeStep = (id: string) => onChange(steps.filter((s) => s.id !== id));
  const setScale = (id: string, scale: number) =>
    onChange(steps.map((s) => (s.id === id ? { ...s, params: { ...s.params, scale } } : s)));

  const usedTypes = steps.map((s) => s.type);
  const addOptions = STEP_CATALOG.filter((c) => !usedTypes.includes(c.type)).map((c) => ({
    key: c.type,
    value: formatMessage(messages[c.labelId]),
  }));

  const addStep = (type: string) => {
    const base: SequenceStepUI = { id: uuidv4(), type, enabled: true };
    if (type === 'upscale') base.params = { scale: 2 };
    onChange([...steps, base]);
  };

  return (
    <div className={styles.builder}>
      {steps.length === 0 ? (
        <p className={styles.empty}>{formatMessage(messages.aiEditSequenceNeedsStep)}</p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={steps.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            <div className={styles.list}>
              {steps.map((step) => (
                <SortableRow
                  key={step.id}
                  step={step}
                  disabled={disabled}
                  onToggle={toggleStep}
                  onRemove={removeStep}
                  onScaleChange={setScale}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {addOptions.length > 0 && (
        <Dropdown
          className={styles.addStep}
          options={addOptions}
          disabled={disabled}
          dropdownDirection="up"
          reduceOptionSize
          onClick={(key) => addStep(String(key))}
        >
          {formatMessage(messages.aiEditAddStep)}
        </Dropdown>
      )}
    </div>
  );
};

export default SequenceBuilder;
